import { useState, useEffect, useMemo } from 'react'
import { useFullscreen } from '@renderer/utils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import cogoToast from '@successtar/cogo-toast'

interface FullscreenManagerReturn {
  isFullscreen: boolean
  isStartupGracePeriodActive: boolean
  showSubmitPinModal: boolean
  setShowSubmitPinModal: (show: boolean) => void
  showChangePinModal: boolean
  setShowChangePinModal: (show: boolean) => void
  handleFullscreenToggle: () => void
  handlePinSubmit: (pin: string) => void
  handlePinChange: (newPin: string) => void
}

export const useFullscreenManager = (): FullscreenManagerReturn => {
  const { isFullscreen, toggleFullscreen } = useFullscreen()
  const { password, setPassword } = useSettingsStore()
  const [showSubmitPinModal, setShowSubmitPinModal] = useState<boolean>(false)
  const [showChangePinModal, setShowChangePinModal] = useState<boolean>(false)

  // Startup grace period: allow exiting fullscreen / opening settings without PIN for a short window.
  // This prevents "no keyboard + forgot PIN" lockout situations right after app launch.
  const GRACE_PERIOD_MS = 10_000
  const graceEndAt = useMemo(() => Date.now() + GRACE_PERIOD_MS, [])
  const [now, setNow] = useState<number>(() => Date.now())

  useEffect(() => {
    if (!isFullscreen) return
    const id = window.setInterval(() => setNow(Date.now()), 250)
    return (): void => window.clearInterval(id)
  }, [isFullscreen])

  const isStartupGracePeriodActive = isFullscreen && now < graceEndAt

  // Reset PIN modal when exiting fullscreen without using the toggle
  useEffect(() => {
    if (!isFullscreen) {
      setShowSubmitPinModal(false)
    }
  }, [isFullscreen])

  const handleFullscreenToggle = async (): Promise<void> => {
    if (!isFullscreen) {
      await toggleFullscreen()
      setShowSubmitPinModal(false)
      return
    }

    if (password && !isStartupGracePeriodActive) {
      setShowSubmitPinModal(true)
    } else {
      await toggleFullscreen()
    }
  }

  const handlePinSubmit = async (pin: string): Promise<void> => {
    if (pin === password) {
      await toggleFullscreen()
      setShowSubmitPinModal(false)
    } else {
      cogoToast.error('Incorrect PIN. Please try again.')
    }
  }

  const handlePinChange = (newPin: string): void => {
    setPassword(newPin)
    setShowChangePinModal(false)
    cogoToast.success('PIN updated successfully')
  }

  return {
    isFullscreen,
    isStartupGracePeriodActive,
    showSubmitPinModal,
    setShowSubmitPinModal,
    showChangePinModal,
    setShowChangePinModal,
    handleFullscreenToggle,
    handlePinSubmit,
    handlePinChange
  }
}
